import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin } from '../../../contexts/PermissionsContext';

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'link'];

const isHtmlEmpty = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
};

const slugify = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

const EventFormPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const isEditMode = !!eventId;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form fields
  const [name, setName] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [description, setDescription] = useState('');
  const [priceInDollars, setPriceInDollars] = useState('');
  const [totalSlots, setTotalSlots] = useState('');
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [registrationOpenDate, setRegistrationOpenDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingTimeoutMinutes, setPendingTimeoutMinutes] = useState(15);
  const [showWaiver, setShowWaiver] = useState(true);
  const [customFieldEnabled, setCustomFieldEnabled] = useState(false);
  const [customFieldLabel, setCustomFieldLabel] = useState('');

  const quillModules = useMemo(() => QUILL_MODULES, []);
  const quillFormats = useMemo(() => QUILL_FORMATS, []);

  const slugPreview = useMemo(() => slugify(name), [name]);

  const fetchEvent = useCallback(async () => {
    if (!isEditMode) return;
    setLoading(true);
    setError(null);
    try {
      const event = await unifiedApi.getEvent(eventId);
      if (!event) {
        setError('Event not found.');
        return;
      }
      setName(event.name);
      setExcerpt(event.excerpt || '');
      setDescription(event.description || '');
      setPriceInDollars((event.priceInCents / 100).toFixed(2));
      setTotalSlots(String(event.totalSlots));
      setRegistrationOpen(event.registrationOpen);
      setRegistrationOpenDate(event.registrationOpenDate ? event.registrationOpenDate.slice(0, 16) : '');
      setEndDate(event.endDate ? event.endDate.slice(0, 16) : '');
      setPendingTimeoutMinutes(event.pendingTimeoutMinutes);
      setShowWaiver(event.showWaiver ?? true);
      setCustomFieldEnabled(event.customFieldEnabled ?? false);
      setCustomFieldLabel(event.customFieldLabel || '');
    } catch {
      setError('Failed to load event.');
    } finally {
      setLoading(false);
    }
  }, [eventId, isEditMode]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Event name is required.');
      return;
    }
    if (!totalSlots || parseInt(totalSlots) < 1) {
      setError('Total slots must be at least 1.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    const data = {
      name: name.trim(),
      excerpt: isHtmlEmpty(excerpt) ? null : excerpt,
      description: isHtmlEmpty(description) ? null : description,
      priceInCents: Math.round(parseFloat(priceInDollars || '0') * 100),
      totalSlots: parseInt(totalSlots),
      registrationOpen,
      registrationOpenDate: registrationOpenDate || null,
      endDate: endDate || null,
      pendingTimeoutMinutes,
      showWaiver,
      customFieldEnabled,
      customFieldLabel: customFieldEnabled ? (customFieldLabel.trim() || null) : null,
    };

    try {
      if (isEditMode) {
        await unifiedApi.updateEvent(eventId, data);
        setSuccess('Event updated successfully.');
      } else {
        const created = await unifiedApi.createEvent(data);
        setSuccess('Event created successfully.');
        navigate(`/admin/events/${created.id}/edit`, { replace: true });
      }
    } catch (err) {
      setError(err?.message || `Failed to ${isEditMode ? 'update' : 'create'} event.`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/events')}
          sx={{ mb: 2 }}
        >
          Back to Events
        </Button>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Edit Event' : 'Create Event'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Stack spacing={3}>
        {/* Basic Info */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Event Details</Typography>
          <Stack spacing={2}>
            <TextField
              label="Event Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              disabled={!isAdmin}
              inputProps={{ maxLength: 200 }}
            />

            {name && (
              <Typography variant="body2" color="text.secondary">
                Slug preview: <code>{slugPreview}</code>
              </Typography>
            )}

            <Box sx={{
              '& .ql-container': { minHeight: 80, fontSize: '1rem' },
              '& .ql-editor': { minHeight: 80 },
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Excerpt (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Short summary shown on the registration home page. Falls back to the description if left blank.
              </Typography>
              <ReactQuill
                theme="snow"
                value={excerpt}
                onChange={setExcerpt}
                modules={quillModules}
                formats={quillFormats}
                readOnly={!isAdmin}
                placeholder="A short teaser shown on the registration listing…"
              />
            </Box>

            <Box sx={{
              '& .ql-container': { minHeight: 120, fontSize: '1rem' },
              '& .ql-editor': { minHeight: 120 },
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Description (optional)
              </Typography>
              <ReactQuill
                theme="snow"
                value={description}
                onChange={setDescription}
                modules={quillModules}
                formats={quillFormats}
                readOnly={!isAdmin}
                placeholder="Describe your event — location, schedule, what to expect..."
              />
            </Box>
          </Stack>
        </Paper>

        {/* Pricing & Capacity */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Pricing &amp; Capacity</Typography>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
            <TextField
              label="Price"
              type="number"
              value={priceInDollars}
              onChange={(e) => setPriceInDollars(e.target.value)}
              disabled={!isAdmin}
              sx={{ maxWidth: 200 }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 },
                },
              }}
            />
            <TextField
              label="Total Slots"
              type="number"
              value={totalSlots}
              onChange={(e) => setTotalSlots(e.target.value)}
              required
              disabled={!isAdmin}
              sx={{ maxWidth: 200 }}
              slotProps={{ input: { inputProps: { min: 1, max: 100000 } } }}
            />
          </Stack>
        </Paper>

        {/* Registration Settings */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Registration Settings</Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={registrationOpen}
                  onChange={(e) => setRegistrationOpen(e.target.checked)}
                  color="success"
                  disabled={!isAdmin}
                />
              }
              label={registrationOpen ? 'Registration Open' : 'Registration Closed'}
            />

            <TextField
              label="Scheduled Open Date"
              type="datetime-local"
              value={registrationOpenDate}
              onChange={(e) => setRegistrationOpenDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Optional. Registration won't show as open until this date."
              fullWidth
              disabled={!isAdmin}
              sx={{ maxWidth: 400 }}
            />

            <TextField
              label="Event End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Optional. After this date the event is hidden from the registration page and registration is closed."
              fullWidth
              disabled={!isAdmin}
              sx={{ maxWidth: 400 }}
            />

            <TextField
              label="Pending Timeout (minutes)"
              type="number"
              value={pendingTimeoutMinutes}
              onChange={(e) => setPendingTimeoutMinutes(parseInt(e.target.value) || 15)}
              helperText="Time before an unpaid registration expires."
              slotProps={{ input: { inputProps: { min: 1, max: 1440 } } }}
              disabled={!isAdmin}
              sx={{ maxWidth: 200 }}
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={showWaiver}
                    onChange={(e) => setShowWaiver(e.target.checked)}
                    disabled={!isAdmin}
                  />
                }
                label={showWaiver ? 'Show Waiver' : 'Hide Waiver'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                When on, the organization waiver is shown on this event&apos;s registration page and must be accepted.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Custom Field */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Custom Field</Typography>
          <Stack spacing={2}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={customFieldEnabled}
                    onChange={(e) => setCustomFieldEnabled(e.target.checked)}
                    disabled={!isAdmin}
                  />
                }
                label={customFieldEnabled ? 'Custom Field Enabled' : 'Custom Field Disabled'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Adds an extra free-text box to this event&apos;s registration form.
              </Typography>
            </Box>

            {customFieldEnabled && (
              <TextField
                label="Field Label"
                value={customFieldLabel}
                onChange={(e) => setCustomFieldLabel(e.target.value)}
                placeholder="e.g. Foursome for your golf tournament:"
                helperText="The label shown above the registrant's text box."
                fullWidth
                disabled={!isAdmin}
                inputProps={{ maxLength: 200 }}
                sx={{ maxWidth: 500 }}
              />
            )}
          </Stack>
        </Paper>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !isAdmin}
          >
            {saving ? 'Saving…' : isEditMode ? 'Save Changes' : 'Create Event'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/events')}
          >
            Cancel
          </Button>
        </Box>
      </Stack>
    </AdminLayout>
  );
};

export default EventFormPage;
